import React from 'react';
import { ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react';
import { Skeleton } from '@components/atoms/Skeleton';
import { cn } from '@utils/cn';
import { DataTableColumn, DataTableProps, SortDirection } from './DataTable.types';

const alignClass = {
  left: 'text-left',
  right: 'text-right',
  center: 'text-center',
} as const;

const SortIcon: React.FC<{ active: boolean; direction: SortDirection }> = ({
  active,
  direction,
}) => {
  if (!active) {
    return (
      <ArrowUpDown
        size={13}
        className="opacity-0 group-hover:opacity-40 transition-opacity"
        aria-hidden
      />
    );
  }
  const Icon = direction === 'asc' ? ArrowUp : ArrowDown;
  return <Icon size={13} className="text-indigo-600" aria-hidden />;
};

/**
 * A scannable table for list surfaces.
 *
 * Deliberately unopinionated about data: it sorts nothing and fetches nothing.
 * The parent owns paging, sorting and filtering — usually server-side — and this
 * renders one page of whatever it is handed.
 *
 * Wide content scrolls inside its own container so the page body never scrolls
 * sideways, and rows keep their height while loading so the layout does not jump.
 */
export function DataTable<T>({
  columns,
  rows,
  rowKey,
  onRowClick,
  isRowHighlighted,
  sort,
  onSortChange,
  isLoading = false,
  loadingRowCount = 8,
  emptyState,
  caption,
  className,
}: DataTableProps<T>) {
  const isInteractive = Boolean(onRowClick);

  const handleSort = (column: DataTableColumn<T>) => {
    if (!column.sortable || !onSortChange) return;
    const isActive = sort?.key === column.key;
    onSortChange({
      key: column.key,
      // Re-clicking the active column flips it; a new column starts descending,
      // which is what people expect from dates and amounts.
      direction: isActive && sort?.direction === 'desc' ? 'asc' : 'desc',
    });
  };

  const showEmptyState = !isLoading && rows.length === 0;

  return (
    <div
      className={cn(
        'overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700',
        className,
      )}>
      <table className="w-full min-w-[720px] border-collapse">
        {caption && <caption className="sr-only">{caption}</caption>}
        <thead>
          <tr className="bg-gray-50 dark:bg-gray-800">
            {columns.map((column) => {
              const isActive = sort?.key === column.key;
              return (
                <th
                  key={column.key}
                  scope="col"
                  style={column.width ? { width: column.width } : undefined}
                  aria-sort={
                    isActive
                      ? sort?.direction === 'asc'
                        ? 'ascending'
                        : 'descending'
                      : undefined
                  }
                  className={cn(
                    'px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wider whitespace-nowrap',
                    'text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700',
                    alignClass[column.align ?? 'left'],
                    column.hideOnSmall && 'hidden md:table-cell',
                  )}>
                  {column.sortable && onSortChange ? (
                    <button
                      type="button"
                      onClick={() => handleSort(column)}
                      className={cn(
                        'group inline-flex items-center gap-1.5 rounded',
                        'hover:text-gray-900 dark:hover:text-white',
                        'focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500',
                        isActive && 'text-gray-900 dark:text-white',
                        column.align === 'right' && 'flex-row-reverse',
                      )}>
                      {column.header}
                      <SortIcon active={isActive} direction={sort?.direction ?? 'desc'} />
                    </button>
                  ) : (
                    column.header
                  )}
                </th>
              );
            })}
          </tr>
        </thead>

        <tbody>
          {isLoading &&
            Array.from({ length: loadingRowCount }).map((_, rowIndex) => (
              <tr
                key={`skeleton-${rowIndex}`}
                className="border-b border-gray-100 dark:border-gray-800">
                {columns.map((column) => (
                  <td
                    key={column.key}
                    className={cn(
                      'px-4 py-3',
                      column.hideOnSmall && 'hidden md:table-cell',
                    )}>
                    <Skeleton height={14} width={column.align === 'right' ? '60%' : '80%'} />
                  </td>
                ))}
              </tr>
            ))}

          {!isLoading &&
            rows.map((row) => {
              const highlighted = isRowHighlighted?.(row) ?? false;
              return (
                <tr
                  key={rowKey(row)}
                  onClick={isInteractive ? () => onRowClick?.(row) : undefined}
                  onKeyDown={
                    isInteractive
                      ? (event) => {
                          // A cell can hold its own control (the attachments
                          // button, say). Its keydown bubbles here, and
                          // stopPropagation on *click* does nothing for it — so
                          // ignore anything that did not start on the row
                          // itself, or Enter would suppress the button's own
                          // activation and open the wrong thing.
                          if (event.target !== event.currentTarget) return;
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            onRowClick?.(row);
                          }
                        }
                      : undefined
                  }
                  tabIndex={isInteractive ? 0 : undefined}
                  // Deliberately no role="button": a <tr> announced as a button
                  // strips the table semantics screen readers rely on, and it is
                  // invalid ARIA once the row contains its own button.
                  aria-label={isInteractive ? 'Open row' : undefined}
                  className={cn(
                    'group border-b border-gray-100 dark:border-gray-800 transition-colors duration-100',
                    'focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-indigo-500',
                    // Hover has to be unmistakable: a whole row is a big click
                    // target, and the user needs to know which one they are on.
                    isInteractive &&
                      'cursor-pointer hover:bg-indigo-50/70 dark:hover:bg-indigo-900/20',
                    highlighted && 'bg-amber-50/60 dark:bg-amber-900/10',
                    highlighted && isInteractive && 'hover:bg-amber-100/70 dark:hover:bg-amber-900/25',
                  )}>
                  {columns.map((column, columnIndex) => (
                    <td
                      key={column.key}
                      className={cn(
                        'px-4 py-3 text-sm text-gray-900 dark:text-gray-100 align-middle',
                        alignClass[column.align ?? 'left'],
                        column.hideOnSmall && 'hidden md:table-cell',
                        // The accent rail lives on the first cell so it reads as a
                        // property of the row, not a column of its own.
                        highlighted &&
                          columnIndex === 0 &&
                          'shadow-[inset_3px_0_0_0_theme(colors.amber.500)]',
                        column.cellClassName,
                      )}>
                      {column.render(row)}
                    </td>
                  ))}
                </tr>
              );
            })}
        </tbody>
      </table>

      {showEmptyState && emptyState && <div className="p-6">{emptyState}</div>}
    </div>
  );
}

export type { DataTableColumn, DataTableProps, DataTableSort } from './DataTable.types';
